import React, { useState } from 'react'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { Button } from '@duck/ui'
import { apis } from '@/modules/apiClients'
import { useTar, TranslationKey } from '@/modules/i18n'
import { toast } from 'sonner'

interface DownloadPDFButtonProps {
  challengeId: string
  disabled?: boolean
  variant?: 'default' | 'outline'
  size?: 'default' | 'sm' | 'lg'
}

export const DownloadPDFButton: React.FC<DownloadPDFButtonProps> = ({
  challengeId,
  disabled = false,
  variant = 'outline',
  size = 'sm',
}) => {
  const { t } = useTar()
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    if (!challengeId || disabled || isDownloading) return

    setIsDownloading(true)
    try {
      const response = await apis.challengeService.getFeedbackPdf(challengeId, {
        responseType: 'blob',
      })

      const blob = response.data as Blob
      const contentDisposition = response.headers['content-disposition']

      // Prefer RFC 5987 / 6266 filename* (UTF-8, percent-encoded), fall back to plain filename
      let fileName: string | undefined

      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
        if (filenameStarMatch?.[1]) {
          try {
            fileName = decodeURIComponent(filenameStarMatch[1])
          } catch {
            // Ignore decode errors and fall back to basic filename
          }
        }

        if (!fileName) {
          const fileNameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/)
          fileName = fileNameMatch?.[1]
        }
      }

      fileName = fileName ?? `feedback_${challengeId}.pdf`

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      toast.error(t('challenges.failedToDownloadPdf'))
    } finally {
      setIsDownloading(false)
    }
  }

  const generatingLabel = t('common.generating')

  return (
    <Button
      variant={variant}
      size={size}
      disabled={disabled || isDownloading}
      onClick={handleDownload}
      className="shrink-0"
      aria-busy={isDownloading}
      aria-disabled={disabled || isDownloading}
    >
      {isDownloading ? (
        <>
          <svg className="animate-spin h-4 w-4 mr-1.5" viewBox="0 0 24 24" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          {generatingLabel}
          <span className="sr-only" role="status" aria-live="polite">
            {generatingLabel}
          </span>
        </>
      ) : (
        <>
          <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
          {t('challenges.downloadFeedbackPdf')}
        </>
      )}
    </Button>
  )
}
